import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Label = ({ children }) => (
  <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#55554F]">{children}</span>
);

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? "authed" : "checking");
  const [enquiries, setEnquiries] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const loadEnquiries = () => {
    axios
      .get(`${API}/admin/enquiries`, { withCredentials: true })
      .then((res) => { setEnquiries(res.data); setForbidden(false); })
      .catch((err) => {
        if (err.response?.status === 403) { setForbidden(true); setEnquiries([]); }
        else setEnquiries([]);
      });
  };

  useEffect(() => {
    if (location.state?.user) { loadEnquiries(); return; }
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => { setUser(res.data); setAuthState("authed"); loadEnquiries(); })
      .catch(() => setAuthState("guest"));
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setAuthState("guest");
    setEnquiries(null);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2F0EB]">
      <header className="border-b border-white/10 px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="/" data-testid="admin-logo" className="font-display text-lg tracking-[0.15em]">ETIA MEDIA</a>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A8A85]">Admin</span>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
        {authState === "checking" && (
          <p data-testid="admin-loading" className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8A8A85]">Checking access…</p>
        )}

        {authState === "guest" && (
          <div className="max-w-md" data-testid="admin-login-card">
            <h1 className="font-display text-5xl leading-[0.95]">Owner<br />sign-in.</h1>
            <p className="mt-6 text-[#8A8A85] leading-relaxed">
              This area is private. Sign in with Google to view your enquiries.
            </p>
            <button
              data-testid="admin-google-signin-btn"
              onClick={login}
              className="mt-10 bg-[#F2F0EB] text-[#0A0A0A] font-display text-sm tracking-[0.08em] px-10 py-4 rounded-full hover:scale-105 transition-transform duration-300"
            >
              Continue with Google
            </button>
          </div>
        )}

        {authState === "authed" && user && (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {user.picture && <img src={user.picture} alt="" className="w-11 h-11 rounded-full border border-white/15" />}
                <div>
                  <h1 className="font-display text-2xl">Enquiries</h1>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A85] mt-1" data-testid="admin-user-email">{user.email}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="admin-refresh-btn"
                  onClick={loadEnquiries}
                  className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A85] hover:text-[#F2F0EB] hover:border-white/30 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                <button
                  data-testid="admin-logout-btn"
                  onClick={logout}
                  className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A85] hover:text-[#F2F0EB] hover:border-white/30 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            </div>

            {forbidden && (
              <div data-testid="admin-forbidden" className="mt-12 border border-white/10 p-10 text-center">
                <p className="font-display text-xl">This area is for Jacques only.</p>
                <p className="text-sm text-[#8A8A85] mt-3">You're signed in as {user.email}. Sign out and use the owner account.</p>
              </div>
            )}

            {!forbidden && enquiries && enquiries.length === 0 && (
              <p data-testid="admin-empty" className="mt-12 text-[#8A8A85]">No enquiries yet — they'll land here the moment someone submits the form.</p>
            )}

            {!forbidden && enquiries && enquiries.length > 0 && (
              <div className="mt-10 space-y-px bg-white/10 border border-white/10" data-testid="admin-enquiry-list">
                {enquiries.map((e) => (
                  <div key={e.id} data-testid={`admin-enquiry-${e.id.slice(0, 8)}`} className="bg-[#0A0A0A] hover:bg-[#111111] p-6 transition-colors duration-300">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div className="flex items-baseline gap-4">
                        <span className="font-display text-lg">{e.name}</span>
                        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F2F0EB] border border-white/20 px-2.5 py-1">{e.project_type}</span>
                      </div>
                      <span className="font-mono2 text-[10px] tracking-[0.15em] text-[#55554F]">
                        {new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </div>
                    <div className="mt-3 grid sm:grid-cols-3 gap-3">
                      <div><Label>Email</Label><p className="text-sm text-[#F2F0EB] mt-1">{e.email}</p></div>
                      {e.budget && <div><Label>Budget</Label><p className="text-sm text-[#F2F0EB] mt-1">{e.budget}</p></div>}
                      {e.package_interest && <div><Label>Setup</Label><p className="text-sm text-[#F2F0EB] mt-1">{e.package_interest}</p></div>}
                    </div>
                    {e.message && <p className="mt-3 text-sm text-[#8A8A85] leading-relaxed border-t border-white/5 pt-3">{e.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
